// consult-form.tsx
import { Container, Flex, Heading, Section, Text } from "@radix-ui/themes";
import styles from "./consult-form.module.css";
import { Link } from "~/components/RouterLink";
import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { BaseConsultForm } from "~/components/BaseConsultForm";

type ConsultFormLocalization = {
  getAFreeConsultation: string;
  yourFirstConsultationIsFree: string;
  ourLocation: string;
  // Add more fields if needed
};

const englishConsultFormLocalization: ConsultFormLocalization = {
  getAFreeConsultation: "Get a Free Consultation Appointment",
  yourFirstConsultationIsFree:
    "Your first consultation visit at Miami Orthodontic Specialists is always free. Start your smile journey by filling out this form.",
  ourLocation: "Our Location",
};

const spanishConsultFormLocalization: ConsultFormLocalization = {
  getAFreeConsultation: "Obtenga una cita de consulta gratuita",
  yourFirstConsultationIsFree:
    "Tu primera visita de consulta en Miami Orthodontic Specialists siempre es gratuita. Comienza tu camino hacia una sonrisa completando este formulario.",
  ourLocation: "Nuestra ubicación",
};

const localized: LocalizedStrings<ConsultFormLocalization> = {
  [Locale.EN]: englishConsultFormLocalization,
  [Locale.ES]: spanishConsultFormLocalization,
};

const homepageLocalized: LocalizedStrings<ConsultFormLocalization> = {
  [Locale.EN]: {
    ...englishConsultFormLocalization,
    yourFirstConsultationIsFree:
      "Ready to start your smile journey? Fill out the form below to schedule your consultation with Miami Orthodontic Specialists.",
  },
  [Locale.ES]: {
    ...spanishConsultFormLocalization,
    yourFirstConsultationIsFree:
      "¿Listo para comenzar el camino hacia tu nueva sonrisa? Completa el formulario a continuación para programar tu consulta con Miami Orthodontic Specialists.",
  },
};

type HomeConsultFormProps = {
  introductionVariant?: "default" | "homepage";
  source?: "home" | "contact-us";
};

export function HomeConsultForm({
  introductionVariant = "default",
  source = "home",
}: HomeConsultFormProps) {
  const getLocalization = useLocalization(
    introductionVariant === "homepage" ? homepageLocalized : localized
  );

  return (
    <Section pt="0" pb="0">
      <Container>
        <Flex
          direction={{ initial: "column", sm: "row" }}
          width="100%"
          justify="between"
          align={{ initial: "center", sm: "start" }}
          gapY="8"
        >
          <Flex
            direction="column"
            flexBasis={{ initial: "100%", sm: "50%" }}
            gapY="6"
          >
            <img
              width="350"
              height="141"
              src="/optimized/miami-ortho-logo-350.webp"
              alt="Logo of Miami Orthodontic Specialists"
              loading="lazy"
              decoding="async"
            />
            <Flex direction="column" gapY="2">
              <Text size="4" weight="bold">
                {getLocalization("ourLocation")}
              </Text>
              <Link
                className={styles.link}
                to="https://goo.gl/maps/uKex5kBv4Sqja5iY9"
              >
                8525 SW 92nd Street, Suite B-9{" "}
                <br />
                Miami, FL 33156
              </Link>
            </Flex>
            <Link className={styles.link} to="tel:305-407-9000">
              305-407-9000
            </Link>
          </Flex>
          <Flex
            direction="column"
            flexBasis={{ initial: "100%", sm: "50%" }}
            gapY="5"
            px={{ initial: "6", sm: "0" }}
          >
            <Heading as="h2" className={styles.consultFormHeading}>
              {getLocalization("getAFreeConsultation")}
            </Heading>
            <Text size="4">
              {getLocalization("yourFirstConsultationIsFree")}
            </Text>
            <BaseConsultForm source={source} />
          </Flex>
        </Flex>
      </Container>
    </Section>
  );
}
